/**
 * The sign-up form's mobile number: a country-code picker plus the digits the person types.
 * The API still takes one E.164 string (`+919876543210`) — the calling code is what it uses to set
 * up the company's country, currency and time zone — so this builds it from the two pieces and the
 * contract does not change.
 */

export interface DialCountry {
  /** Calling code without the plus. */
  code: string;
  /** ISO 3166 alpha-2, which picks the flag. */
  iso: string;
  name: string;
  /** What a mobile number looks like AFTER the leading 0 (the national trunk prefix) is dropped. */
  national: RegExp;
  /** Shown when the digits do not fit `national`. */
  problem: string;
}

/** The countries sign-up can set a company up in (the server's calling-code table), India first. */
export const SIGNUP_DIAL_CODES: readonly DialCountry[] = [
  {
    code: '91',
    iso: 'IN',
    name: 'India',
    national: /^[6-9]\d{9}$/,
    problem: 'Enter a valid 10-digit mobile number starting with 6, 7, 8 or 9.',
  },
  {
    code: '1',
    iso: 'US',
    name: 'United States / Canada',
    national: /^[2-9]\d{2}[2-9]\d{6}$/,
    problem: 'Enter a valid 10-digit number, like 415 555 0132.',
  },
  {
    code: '44',
    iso: 'GB',
    name: 'United Kingdom',
    national: /^7\d{9}$/,
    problem: 'Enter a valid UK mobile number, like 07700 900123.',
  },
  {
    code: '971',
    iso: 'AE',
    name: 'United Arab Emirates',
    national: /^5\d{8}$/,
    problem: 'Enter a valid UAE mobile number, like 50 123 4567.',
  },
  {
    code: '65',
    iso: 'SG',
    name: 'Singapore',
    national: /^[89]\d{7}$/,
    problem: 'Enter a valid 8-digit Singapore mobile number starting with 8 or 9.',
  },
  {
    code: '61',
    iso: 'AU',
    name: 'Australia',
    national: /^4\d{8}$/,
    problem: 'Enter a valid Australian mobile number, like 0412 345 678.',
  },
  {
    code: '49',
    iso: 'DE',
    name: 'Germany',
    national: /^1[5-7]\d{8,9}$/,
    problem: 'Enter a valid German mobile number, like 0151 2345 6789.',
  },
  {
    code: '31',
    iso: 'NL',
    name: 'Netherlands',
    national: /^6\d{8}$/,
    problem: 'Enter a valid Dutch mobile number, like 06 1234 5678.',
  },
];

export const DEFAULT_DIAL_CODE = '91';

/**
 * E.164 from the chosen calling code and whatever was typed. Forgiving about how people write a
 * number: spaces, dashes, dots and brackets are dropped; a leading `0` (the national trunk prefix,
 * `09876543210`) is dropped; a number typed with its own `+` or `00` country code is kept as it is
 * (a pasted `+44 7700 900123` wins over the dropdown); and for India, `91 98765 43210` is not
 * given a second 91. Returns '' when no digits were typed, so the form's "required" message shows.
 */
export function buildE164(dialCode: string, typed: string): string {
  const trimmed = typed.trim();
  const digits = trimmed.replace(/\D/g, '');
  if (!digits) return '';
  if (trimmed.startsWith('+')) return `+${digits}`;
  if (digits.startsWith('00')) return `+${digits.slice(2)}`;
  if (dialCode === '91' && digits.length === 12 && digits.startsWith('91')) return `+${digits}`;
  return `+${dialCode}${digits.replace(/^0+/, '')}`;
}

/**
 * Why an E.164 number is not a mobile number, or null when it is. For the countries sign-up knows,
 * the digits after the calling code must look like a mobile there (India: 10 digits starting 6-9).
 * Any other calling code only has to be a well-formed E.164 number, which is all the API checks.
 */
export function mobileProblem(e164: string): string | null {
  const generic = 'Enter your full mobile number: digits only, 8 to 15 including the country code.';
  if (!/^\+[1-9]\d*$/.test(e164)) return generic;
  const digits = e164.slice(1);
  const country = [...SIGNUP_DIAL_CODES]
    .sort((a, b) => b.code.length - a.code.length)
    .find((c) => digits.startsWith(c.code));
  // A known country says what ITS numbers look like, even for a number that is far too short.
  if (country)
    return country.national.test(digits.slice(country.code.length)) ? null : country.problem;
  return /^\+[1-9]\d{7,14}$/.test(e164) ? null : generic;
}

/**
 * Autofill and paste can put a whole international number into the national-number field:
 * "+91 98765 43210", "0091 98765-43210", "(+44) 7700 900123", "919876543210". When the text carries
 * a calling code the sign-up knows, this splits it between the country picker and the field;
 * anything else comes back untouched, so what a person is typing is never rewritten under them.
 *
 *  - an explicit `+` or `00` prefix: any code in SIGNUP_DIAL_CODES (longest match, so 971 is not 97)
 *    moves to the picker and the rest becomes the field's digits;
 *  - no prefix: only the code ALREADY selected is recognised, and only when what follows is a whole
 *    national number for that country (India: `91` + 10 digits starting 6-9) — a bare "65..." is as
 *    likely an Indian number as a Singapore one, so another country's code needs its `+`.
 */
export function splitInternational(
  dialCode: string,
  typed: string,
): { dialCode: string; national: string } {
  const unchanged = { dialCode, national: typed };
  const compact = typed.replace(/[\s\-.()]/g, '');
  if (!/^\+?\d+$/.test(compact)) return unchanged;

  const explicit = compact.startsWith('+') || compact.startsWith('00');
  const digits = compact.replace(/^\+|^00/, '');
  if (!digits) return unchanged;

  if (explicit) {
    const country = [...SIGNUP_DIAL_CODES]
      .sort((a, b) => b.code.length - a.code.length)
      .find((c) => digits.startsWith(c.code));
    if (!country) return unchanged;
    return { dialCode: country.code, national: digits.slice(country.code.length) };
  }

  const selected = SIGNUP_DIAL_CODES.find((c) => c.code === dialCode);
  if (selected && digits.startsWith(selected.code)) {
    const rest = digits.slice(selected.code.length);
    if (selected.national.test(rest)) return { dialCode, national: rest };
  }
  return unchanged;
}
